import { Address } from '@pelicargo/types';
import { escapeRegExp, omit } from 'lodash';

import { COUNTRY_NAME_MAP, isoStateToName } from '../constants';

export const formatAddress = (address: Address, options: { direction?: 'row' | 'column' } = { direction: 'row' }) => {
  const isoState = isoStateToName(address?.state) ?? address?.state;
  const isoCountry = COUNTRY_NAME_MAP?.[address?.country] ?? address?.country;

  const parts = [
    address?.recipient,
    address?.line_one,
    address?.line_two,
    address?.city,
    isoState,
    address?.zip_code,
    isoCountry,
  ];

  if (options.direction === 'column') {
    const groupOne = [address?.recipient];
    const groupTwo = [address?.line_one, address?.line_two].filter(Boolean).join(' ');
    const groupThree = [address?.city, isoState, address?.zip_code].filter(Boolean).join(', ');
    const groupFour = [isoCountry].filter(Boolean).join(' ');
    return [groupOne, groupTwo, groupThree, groupFour].flat().filter(Boolean).join('\n');
  }

  return parts.filter(Boolean).join(', ');
};

// Returns the address without the zip code component
export const omitZipCode = (address: Address) => {
  if (!address) {
    return {
      recipient: '',
      line_one: '',
      line_two: '',
      country: '',
      state: '',
      city: '',
    };
  }

  const addressWithoutZip = omit(address, ['zip_code']);
  return addressWithoutZip;
};

// Splits the address into two parts, attempting to split at a space, but otherwise keeps the first part `length` characters
// 50 is an arbitrary reasonable default length
export const splitAddress = (address: Address, length: number = 50) => {
  const formatted = formatAddress(address);
  const spaceIndex = formatted.slice(formatted.length / 2).indexOf(' ');

  const splitIndex = spaceIndex === -1 ? length : formatted.length / 2 + spaceIndex;

  return [formatted.slice(0, splitIndex), formatted.slice(splitIndex + 1)];
};

/**
 * The held postal code reduced to what a carrier prints: trimmed, compacted, a US ZIP+4 suffix dropped. Null when it is
 * not a code we can compare on — quotes hold "6066" (a digit lost), "CA" (fields shifted) and "+1" often enough that
 * anything shorter than five alphanumerics is unknown, not a mismatch.
 */
const comparablePostalCode = (zip: string | null | undefined): string | null => {
  const withoutPlusFour = (zip ?? '').trim().replace(/^(\d{5})-\d{4}$/, '$1');
  const compact = withoutPlusFour.replace(/[\s-]/g, '');
  const isPlausible = /^[A-Za-z0-9]{5,10}$/.test(compact) && /\d/.test(compact);
  return isPlausible ? compact : null;
};

/**
 * A pattern finding a postal code in printed text, whatever shape the country gives it — tolerating the spacing of
 * "V8X 0B7" vs "V8X0B7", and a ZIP+4 suffix. Word-bounded, so a phone number's digits never span a match.
 */
const postalCodePattern = (code: string): RegExp => {
  const characters = code.split('').map(escapeRegExp);
  return new RegExp(`\\b${characters.join('[\\s-]?')}(?:-\\d{4})?\\b`, 'i');
};

/**
 * The street numbers in a printed address — "Cargo Building 77", "5932 W. Century Blvd" — excluding the postal code,
 * which is matched on its own. A set, because the parts arrive in no reliable order.
 */
const streetNumbersOf = (text: string, postalCode: RegExp): Set<string> => {
  const withoutPostalCode = text.replace(postalCode, '');
  return new Set(withoutPostalCode.match(/\b\d{1,5}\b/g) ?? []);
};

/**
 * Whether a delivery address a carrier printed is the one we hold, compared on the parts that identify it: the postal
 * code and the street/building numbers. Deliberately NOT a string comparison — the same warehouse is written "AGI / JFK
 * INTL Airport" one place and "Alliance Ground International" another, and a check that cries wolf gets ignored.
 * Undecided (null) rather than false when we hold no postal code to compare on: no evidence is not a mismatch.
 */
export const isSameDeliveryAddress = (printed: string | null | undefined, held: Address): boolean | null => {
  const heldCode = comparablePostalCode(held?.zip_code);
  if (!printed || !heldCode) return null;

  const postalCode = postalCodePattern(heldCode);
  if (!postalCode.test(printed)) return false;

  // Same postal code narrows it to one airport's cargo area, where the building number is what separates the handlers.
  const heldNumbers = streetNumbersOf(formatAddress(held), postalCode);
  if (!heldNumbers.size) return true;
  const printedNumbers = streetNumbersOf(printed, postalCode);
  if (!printedNumbers.size) return true;
  return [...heldNumbers].some((number) => printedNumbers.has(number));
};
